import React, { useState, useEffect, useRef } from 'react';
import axios, { AxiosError } from 'axios';
import { useSearchParams } from 'react-router-dom';
import DataTable from './DataTable';
import SearchBar from './SearchBar';
import AdvancedSearchPanel from './AdvancedSearch';
import { AccountRequestDTO, AdvancedSearchRequestDTO, SearchFieldMetadataDTO } from '../../types'; // Adjust the import path as necessary
import { useAuth } from '../../context/AuthContext';
import Modal from '../Modal';
import AccountRequestForm from '../AccountRequestForm';
import Icon from '@mdi/react';
import { mdiPlus, mdiRefresh, mdiExport, mdiLoading, mdiClose } from '@mdi/js';
import { debounce } from 'lodash';
import './AccountRequests.scss';

const AccountRequests: React.FC = () => {

  const { accessToken } = useAuth();
  const [searchParams, setURLSearchParams] = useSearchParams();
  
  const [resetToken, setResetToken] = useState(0);
  const [data, setData] = useState<AccountRequestDTO[]>([]);
  const [page, setPage] = useState<number>(0);
  const pageRef = useRef<number>(0); // Use ref for immediate access to current page
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [searchParamsState, setSearchParamsState] = useState<string>('');
  const [advancedSearchRequest, setAdvancedSearchRequest] = useState<AdvancedSearchRequestDTO | null>(null);
  const [isUsingAdvancedSearch, setIsUsingAdvancedSearch] = useState<boolean>(false);
  const [sortBy, setSortBy] = useState<string>('dateCreated');
  const [direction, setDirection] = useState<string>('desc');
  const [isModalOpen, setModalOpen] = useState(false);
  const [error, setError] = useState<string|undefined>(); 
  const [version, setVersion] = useState<number>(0);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [isExporting, setIsExporting] = useState(false);
  const [isMenuSlideOut, setIsMenuSlideOut] = useState(false);

  const [isFetching, setIsFetching] = useState(false);
  
  // Advanced search state
  const [showAdvancedSearch, setShowAdvancedSearch] = useState<boolean>(false);
  const [fieldMetadata, setFieldMetadata] = useState<SearchFieldMetadataDTO[]>([]);
  const [isLoadingMetadata, setIsLoadingMetadata] = useState<boolean>(false);

  // URL serialization functions
  const serializeToURL = (simpleSearch: string, advancedSearch: AdvancedSearchRequestDTO | null, isAdvanced: boolean, includeSorting = false) => {
    const params = new URLSearchParams();
    
    if (isAdvanced && advancedSearch) {
      params.set('advancedSearch', 'true');
      
      // Add all advanced search fields to URL
      Object.entries(advancedSearch).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          params.set(key, value.toString());
        }
      });
    } else if (simpleSearch) {
      params.set('search', simpleSearch);
    }
    
    // Only add sorting parameters when explicitly requested (for non-sort operations)
    if (includeSorting) {
      if (sortBy !== 'dateCreated') params.set('sortBy', sortBy);
      if (direction !== 'desc') params.set('direction', direction);
    }
    
    return params;
  };

  const deserializeFromURL = () => {
    const isAdvanced = searchParams.get('advancedSearch') === 'true';
    const simpleSearch = searchParams.get('search') || '';
    
    if (isAdvanced) {
      const advancedRequest: AdvancedSearchRequestDTO = {};
      
      // Map URL params to advanced search request
      const fields = ['requestedBy', 'toEmail', 'toPhone', 'firstName', 'lastName', 'note',
                     'accountNumber', 'token', 'referralSource', 'status', 'type', 'appStatus', 'appType',
                     'externalSource', 'dateCreatedStart', 'dateCreatedEnd', 'id', 'externalId'];
      
      fields.forEach(field => {
        const value = searchParams.get(field);
        if (value) {
          if (field === 'id' || field === 'externalId') {
            (advancedRequest as any)[field] = parseInt(value, 10);
          } else {
            (advancedRequest as any)[field] = value;
          }
        }
      });
      
      return { isAdvanced: true, simpleSearch: '', advancedRequest };
    }
    
    return { isAdvanced: false, simpleSearch, advancedRequest: null };
  };

  const resetChild = () => {
    setSortBy('dateCreated');
    setDirection('desc');
    setResetToken(Date.now()); // Use current timestamp as a unique token
    setSelectedIds(new Set()); // Clear selection when resetting
    setIsUsingAdvancedSearch(false); // Clear advanced search
    setAdvancedSearchRequest(null); // Clear advanced search request
    setShowAdvancedSearch(false); // Close advanced search panel
    setSearchParamsState(''); // Clear simple search
    
    // Clear URL parameters
    setURLSearchParams({}, { replace: true });
  };

  const handleSelectionChange = (newSelectedIds: Set<number>) => {
    setSelectedIds(newSelectedIds);
  };

  const handleClearSelection = () => {
    if (isExporting) return; // Prevent clearing while exporting
    
    setIsMenuSlideOut(true);
    setTimeout(() => {
      setSelectedIds(new Set());
      setIsMenuSlideOut(false);
    }, 300);
  };

  const handleExportSelected = async () => {
    if (selectedIds.size === 0 || isExporting) return;
    
    setIsExporting(true);
    
    try {
      const accountRequestIds = Array.from(selectedIds);
      const response = await axios.post(
        `${process.env.REACT_APP_SERVICE_BASEPATH}/batch-exports/`,
        { accountRequestIds },
        {
          headers: { Authorization: `Bearer ${accessToken}` }
        }
      );
      
      console.log('Batch export created:', response.data);
      
      // Animate menu out, then clear selection
      setIsMenuSlideOut(true);
      setTimeout(() => {
        setSelectedIds(new Set());
        setIsExporting(false);
        setIsMenuSlideOut(false);
      }, 300);
      
    } catch (error) {
      console.error('Failed to create batch export:', error);
      setIsExporting(false);
      // TODO: Show error message
    }
  };
  // Fetch field metadata when advanced search is toggled
  const fetchFieldMetadata = React.useCallback(async () => {
    if (fieldMetadata.length > 0) return; // Already loaded
    
    setIsLoadingMetadata(true);
    try {
      const baseURL = process.env.REACT_APP_SERVICE_BASEPATH;
      
      const response = await fetch(`${baseURL}/account-requests/search/metadata`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const metadata = await response.json();
        setFieldMetadata(metadata);
      } else {
        console.error('Failed to fetch field metadata');
      }
    } catch (error) {
      console.error('Error fetching field metadata:', error);
    } finally {
      setIsLoadingMetadata(false);
    }
  }, [fieldMetadata.length, accessToken]); // Include dependencies

  const refreshData = () => handleSearch(searchParamsState);
  const handleOpenModal = () => setModalOpen(true);
  const handleCloseModal = () => setModalOpen(false);
  
  // Create refs for values that fetchData needs but shouldn't cause re-creation
  const sortByRef = useRef(sortBy);
  const directionRef = useRef(direction);
  const searchParamsStateRef = useRef(searchParamsState);
  const advancedSearchRequestRef = useRef(advancedSearchRequest);
  const isUsingAdvancedSearchRef = useRef(isUsingAdvancedSearch);

  // Update refs when values change
  useEffect(() => {
    sortByRef.current = sortBy;
  }, [sortBy]);

  useEffect(() => {
    directionRef.current = direction;
  }, [direction]);

  useEffect(() => {
    searchParamsStateRef.current = searchParamsState;
  }, [searchParamsState]);

  useEffect(() => {
    advancedSearchRequestRef.current = advancedSearchRequest;
  }, [advancedSearchRequest]);

  useEffect(() => {
    isUsingAdvancedSearchRef.current = isUsingAdvancedSearch;
  }, [isUsingAdvancedSearch]);

  const fetchData = React.useCallback(async () => {
    if (isFetching || !hasMore) return; // Prevent fetching if already fetching or no more data
    setIsFetching(true);
    
    const currentPage = pageRef.current; // Use ref for current page value
    
    try {
      let response: any;
      
      if (isUsingAdvancedSearchRef.current && advancedSearchRequestRef.current) {
        // Use advanced search endpoint
        const searchRequest = {
          ...advancedSearchRequestRef.current,
          pageNumber: currentPage,
          sortBy: sortByRef.current,
          sortDirection: directionRef.current
        };
        
        response = await axios.post(
          `${process.env.REACT_APP_SERVICE_BASEPATH}/account-requests/search/advanced`,
          searchRequest,
          {
            headers: { Authorization: `Bearer ${accessToken}` }
          }
        );
        
        // Handle advanced search response structure
        const results = response.data.results;
        if (results.content.length === 0) {
          setHasMore(false);
        } else {
          setData(prevData => [...prevData, ...results.content]);
          // Increment page in both state and ref
          const nextPage = currentPage + 1;
          setPage(nextPage);
          pageRef.current = nextPage;
          setHasMore(!results.last);
        }
      } else {
        // Use simple search endpoint
        const params: any = { sortBy: sortByRef.current, direction: directionRef.current, pageNumber: currentPage };
        if (searchParamsStateRef.current) {
          params.searchParams = searchParamsStateRef.current;
        }
        
        response = await axios.get(`${process.env.REACT_APP_SERVICE_BASEPATH}/account-requests/`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          params: params,
        });
        
        if (response.data.content.length === 0) {
          setHasMore(false);
        } else {
          setData(prevData => [...prevData, ...response.data.content]);
          // Increment page in both state and ref
          const nextPage = currentPage + 1;
          setPage(nextPage);
          pageRef.current = nextPage;
          if (response.data.last === true) {
            setHasMore(false);
          }
        }
      }
    } catch (error) {
      const axiosError = error as AxiosError; // Cast error to AxiosError
      setError(axiosError.message); // Use AxiosError message or handle differently
      console.error('Failed to fetch data:', axiosError.message);
    } finally {
      setIsFetching(false); // Reset fetching state regardless of success or failure
    }
  }, [isFetching, hasMore, accessToken]);

  useEffect(() => {
    if (!error && version > 0) { // Only fetch data if no error has occurred and version has changed
      fetchData();
    }
  }, [version, fetchData, error]); // Include fetchData and error in dependencies

  // Effect to read URL parameters on mount and URL changes
  useEffect(() => {
    const { isAdvanced, simpleSearch, advancedRequest } = deserializeFromURL();
    
    if (isAdvanced && advancedRequest) {
      // Restore advanced search state from URL
      console.log('AccountRequests: Restoring advanced search from URL:', advancedRequest);
      setAdvancedSearchRequest(advancedRequest);
      setIsUsingAdvancedSearch(true);
      setSearchParamsState('');
      setShowAdvancedSearch(true); // Open advanced search panel
      
      // Fetch field metadata for the advanced search form
      fetchFieldMetadata();
    } else if (simpleSearch) {
      // Restore simple search state from URL
      setSearchParamsState(simpleSearch);
      setIsUsingAdvancedSearch(false);
      setAdvancedSearchRequest(null);
    }
    
    // Restore sorting from URL
    const urlSortBy = searchParams.get('sortBy');
    const urlDirection = searchParams.get('direction');
    if (urlSortBy) setSortBy(urlSortBy);
    if (urlDirection) {
      setDirection(urlDirection);
    } else if (!urlSortBy && !urlDirection) {
      // No sorting parameters in URL means we're using defaults
      setSortBy('dateCreated');
      setDirection('desc');
    }
    
    // Trigger initial data fetch
    if (!error && version === 0) {
      setVersion(1);
    }
  }, [searchParams.toString(), fetchFieldMetadata]); // Re-run when URL changes


  // Debug effect to log advancedSearchRequest changes
  useEffect(() => {
    if (advancedSearchRequest) {
      console.log('AccountRequests: advancedSearchRequest updated:', advancedSearchRequest);
    }
  }, [advancedSearchRequest]);

  // Create a debounced function
  const debouncedSearch = React.useCallback(
    debounce((searchQuery: string) => {
      setData([]);
      setPage(0);
      pageRef.current = 0; // Reset page ref
      setSearchParamsState(searchQuery);
      setHasMore(true);
      
      // Update URL
      const urlParams = serializeToURL(searchQuery, null, false, true);
      setURLSearchParams(urlParams, { replace: true });
      
      setVersion(prev => prev + 1);
    }, 300),
    [serializeToURL, setURLSearchParams] // Include dependencies
  );

  const handleSearch = (searchQuery: string): void => {
    // Switch back to simple search when using the basic search bar
    setIsUsingAdvancedSearch(false);
    setAdvancedSearchRequest(null);
    debouncedSearch(searchQuery);
  };

  // Create a debounced function for advanced search
  const debouncedAdvancedSearch = React.useCallback(
    debounce((searchRequest: AdvancedSearchRequestDTO) => {
      setData([]);
      setPage(0);
      pageRef.current = 0; // Reset page ref
      setAdvancedSearchRequest(searchRequest);
      setIsUsingAdvancedSearch(true);
      setSearchParamsState(''); // Clear simple search when using advanced
      setHasMore(true);
      
      // Update URL
      const urlParams = serializeToURL('', searchRequest, true, true);
      setURLSearchParams(urlParams, { replace: true });
      
      setVersion(prev => prev + 1);
    }, 300),
    [serializeToURL, setURLSearchParams] // Include dependencies
  );

  const handleAdvancedSearch = React.useCallback((searchRequest: AdvancedSearchRequestDTO): void => {
    debouncedAdvancedSearch(searchRequest);
  }, [debouncedAdvancedSearch]);

  const handleAdvancedToggle = () => {
    if (!showAdvancedSearch) {
      fetchFieldMetadata();
    }
    setShowAdvancedSearch(!showAdvancedSearch);
  };

  const handleCloseAdvanced = () => {
    setShowAdvancedSearch(false);
  };

  const handleSort = (newSortBy: string): void => {
    let nextSortBy = sortBy;
    let nextDirection = direction;
    
    if (sortBy === newSortBy) {
      // Cycle through: desc → asc → none (reset to default) → desc
      if (direction === 'desc') {
        nextDirection = 'asc';
      } else if (direction === 'asc') {
        // Reset to default (no sorting)
        nextSortBy = 'dateCreated';
        nextDirection = 'desc';
      } else {
        // From none/default state, start with desc on the clicked column
        nextSortBy = newSortBy;
        nextDirection = 'desc';
      }
    } else {
      // New column, start with desc
      nextSortBy = newSortBy;
      nextDirection = 'desc';
    }
    
    // Update state
    setSortBy(nextSortBy);
    setDirection(nextDirection);
    setData([]);
    setPage(0);
    pageRef.current = 0; // Reset page ref
    setHasMore(true);
    
    // Update URL with the new sorting values (not the old state)
    const urlParams = serializeToURL(searchParamsState, advancedSearchRequest, isUsingAdvancedSearch);
    
    // Add sorting parameters only when different from defaults
    if (nextSortBy !== 'dateCreated') {
      urlParams.set('sortBy', nextSortBy);
    } else {
      urlParams.delete('sortBy');
    }
    
    if (nextDirection !== 'desc') {
      urlParams.set('direction', nextDirection);
    } else {
      urlParams.delete('direction');
    }
    
    setURLSearchParams(urlParams, { replace: true });
    
    setVersion(prev => prev + 1); // Trigger data fetch
  };

  return (
    <div className='account-requests'>
      <Modal isOpen={isModalOpen} onClose={handleCloseModal}>
        <AccountRequestForm handleCloseModal={handleCloseModal} refreshData={refreshData} />
      </Modal>
      <div className='account-requests-options'>
        <SearchBar 
          resetToken={resetToken} 
          onSearch={handleSearch} 
          onAdvancedToggle={handleAdvancedToggle}
          showAdvancedSearch={showAdvancedSearch}
        />
        <div className='button-area'>
        <button className='icon-button icon' onClick={resetChild}><Icon path={mdiRefresh} size={1} />Reset</button>
        <button className='icon-button icon' onClick={handleOpenModal}>New Request <Icon path={mdiPlus} size={1} /></button>
        </div>
      </div>
      
      {/* Advanced Search Panel - Full Width */}
      <AdvancedSearchPanel
        isVisible={showAdvancedSearch}
        onClose={handleCloseAdvanced}
        onSearch={handleAdvancedSearch}
        fieldMetadata={fieldMetadata}
        initialValues={advancedSearchRequest || undefined}
      />
      
      {selectedIds.size > 0 && (
        <div className={`selection-menu ${isMenuSlideOut ? 'slide-out' : ''}`}>
          <div className="selection-menu-content">
            <span className="selection-count">{selectedIds.size} items selected</span>
            <div>
              <button 
                className={`export-btn icon ${isExporting ? 'loading' : ''}`} 
                onClick={handleExportSelected}
                disabled={isExporting}
              >
                <Icon 
                  path={isExporting ? mdiLoading : mdiExport} 
                  size={0.7}
                  spin={isExporting}
                />
                {isExporting ? 'Exporting...' : 'Export'}
              </button>
              <button 
                className="clear-btn icon" 
                onClick={handleClearSelection}
                disabled={isExporting}
              >
                <Icon 
                  path={mdiClose} 
                  size={0.7}
                />
                Clear
              </button>
            </div>
          </div>
        </div>
      )}
      
      <div className="scrollable-content" id="scrollable-content">
        <DataTable 
          data={data} 
          onSort={handleSort} 
          hasMore={hasMore} 
          fetchData={fetchData}
          selectedIds={selectedIds}
          onSelectionChange={handleSelectionChange}
          sortBy={sortBy}
          sortDirection={direction}
        />
      </div>
    </div>
  );
};

export default AccountRequests;
